import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, Target, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, XCircle, RefreshCw, Search, Filter } from "lucide-react"
import { useGoals, useGoalSummary, useDeleteGoal, useRecalculateGoal } from "@/hooks/use-goals"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"
import type { Goal, GoalStatus, GoalMetricType } from "@/lib/types"

const METRIC_LABELS: Record<GoalMetricType, string> = {
  deals_won: "Wygrane transakcje",
  deals_value: "Wartość transakcji",
  activities_count: "Liczba aktywności",
  tasks_completed: "Ukończone zadania",
  revenue: "Przychód",
  calls_made: "Wykonane telefony",
  meetings_held: "Spotkania",
  new_contacts: "Nowe kontakty",
  new_companies: "Nowi kontrahenci",
  custom: "Własna metryka",
}

const PERIOD_LABELS: Record<string, string> = {
  weekly: "Tygodniowy",
  monthly: "Miesięczny",
  quarterly: "Kwartalny",
  yearly: "Roczny",
  custom: "Własny",
}

const STATUS_CONFIG: Record<GoalStatus, { label: string; color: string; icon: React.ElementType }> = {
  active: { label: "Aktywny", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300", icon: Target },
  completed: { label: "Osiągnięty", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300", icon: CheckCircle2 },
  failed: { label: "Nieosiągnięty", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300", icon: XCircle },
  cancelled: { label: "Anulowany", color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300", icon: XCircle },
}

function formatValue(value: number, metricType: string): string {
  if (metricType === "deals_value" || metricType === "revenue") {
    return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 }).format(value)
  }
  return value.toLocaleString("pl-PL")
}

function ProgressBar({ percent, onTrack, status }: { percent: number; onTrack: boolean; status: GoalStatus }) {
  let barColor = "bg-blue-500"
  if (status === "completed") barColor = "bg-green-500"
  else if (status === "failed") barColor = "bg-red-500"
  else if (!onTrack) barColor = "bg-amber-500"

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground">{percent.toFixed(0)}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  )
}

function GoalCard({ goal, onRecalculate }: { goal: Goal; onRecalculate: (id: string) => void }) {
  const navigate = useNavigate()
  const statusCfg = STATUS_CONFIG[goal.status]

  return (
    <div
      className="surface-card p-4 cursor-pointer transition-shadow hover:shadow-md"
      onClick={() => navigate(`/goals/${goal.id}`)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 mr-3">
          <h3 className="font-semibold truncate text-foreground">{goal.name}</h3>
          <p className="text-sm mt-0.5 text-muted-foreground">
            {METRIC_LABELS[goal.metric_type]} &middot; {PERIOD_LABELS[goal.period]}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {goal.status === "active" && !goal.on_track && (
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          )}
          {goal.status === "active" && goal.on_track && (
            <TrendingUp className="h-4 w-4 text-green-500" />
          )}
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
            {statusCfg.label}
          </span>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-lg font-bold tabular-nums text-foreground">
            {formatValue(goal.current_value, goal.metric_type)}
          </span>
          <span className="text-sm tabular-nums text-muted-foreground">
            / {formatValue(goal.target_value, goal.metric_type)}
          </span>
        </div>
        <ProgressBar percent={goal.progress_percent} onTrack={goal.on_track} status={goal.status} />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          {goal.user && (
            <span>{goal.user.first_name} {goal.user.last_name}</span>
          )}
          {goal.scope === "team" && <Badge variant="outline" className="text-xs py-0">Zespół</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {goal.status === "active" && (
            <span>Pozostało {goal.days_remaining} dni</span>
          )}
          <button
            className="p-1 rounded hover:bg-muted/30"
            onClick={(e) => {
              e.stopPropagation()
              onRecalculate(goal.id)
            }}
            title="Przelicz postęp"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}

function SummaryCards({ summary }: { summary: { active: number; completed: number; failed: number; on_track: number; at_risk: number } }) {
  const cards = [
    { label: "Aktywne", value: summary.active, icon: Target, color: "text-blue-500" },
    { label: "Na dobrej drodze", value: summary.on_track, icon: TrendingUp, color: "text-green-500" },
    { label: "Zagrożone", value: summary.at_risk, icon: AlertTriangle, color: "text-amber-500" },
    { label: "Osiągnięte", value: summary.completed, icon: CheckCircle2, color: "text-green-600" },
    { label: "Nieosiągnięte", value: summary.failed, icon: TrendingDown, color: "text-red-500" },
  ]

  return (
    <div className="grid grid-cols-5 gap-3 mb-6">
      {cards.map((card) => (
        <div
          key={card.label}
          className="surface-card p-3 text-center"
        >
          <card.icon className={`h-5 w-5 mx-auto mb-1 ${card.color}`} />
          <p className="text-2xl font-bold text-foreground">{card.value}</p>
          <p className="text-xs text-muted-foreground">{card.label}</p>
        </div>
      ))}
    </div>
  )
}

export function GoalsListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [metricFilter, setMetricFilter] = useState<string>("all")

  const filters = {
    q: search || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    metric_type: metricFilter !== "all" ? metricFilter : undefined,
  }

  const { data: goalsData, isLoading } = useGoals(filters)
  const { data: summaryData } = useGoalSummary()
  const deleteGoal = useDeleteGoal()
  const recalculate = useRecalculateGoal()

  const goals = goalsData?.data ?? []
  const summary = summaryData?.data

  const handleRecalculate = (id: string) => {
    recalculate.mutate(id, {
      onSuccess: () => toast.success("Postęp przeliczony"),
      onError: () => toast.error("Błąd przeliczania"),
    })
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Cele</h1>
          <p className="text-sm mt-1 text-muted-foreground">
            Śledź postępy w realizacji celów zespołu
          </p>
        </div>
        <Button onClick={() => navigate("/goals/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Nowy cel
        </Button>
      </div>

      {summary && <SummaryCards summary={summary} />}

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj celów..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie statusy</SelectItem>
            <SelectItem value="active">Aktywne</SelectItem>
            <SelectItem value="completed">Osiągnięte</SelectItem>
            <SelectItem value="failed">Nieosiągnięte</SelectItem>
            <SelectItem value="cancelled">Anulowane</SelectItem>
          </SelectContent>
        </Select>
        <Select value={metricFilter} onValueChange={setMetricFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Metryka" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie metryki</SelectItem>
            {Object.entries(METRIC_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[160px] rounded-lg" />
          ))}
        </div>
      ) : goals.length === 0 ? (
        <div className="text-center py-16">
          <Target className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-semibold mb-1 text-foreground">Brak celów</h3>
          <p className="text-sm mb-4 text-muted-foreground">
            Utwórz pierwszy cel, aby śledzić postępy zespołu
          </p>
          <Button onClick={() => navigate("/goals/new")}>
            <Plus className="h-4 w-4 mr-2" />
            Nowy cel
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {goals.map((goal) => (
            <GoalCard key={goal.id} goal={goal} onRecalculate={handleRecalculate} />
          ))}
        </div>
      )}
    </div>
  )
}
